/**
 * Little Paper Hub basket components, loaded on every page (layout/theme.liquid) because the cart
 * drawer can morph them in after an add, and morphed-in inline scripts never run.
 *   lph-cart-club: snippets/lph-cart-club.liquid
 *   lph-cart-post: snippets/lph-cart-post.liquid (post direct)
 */

if (!customElements.get('lph-cart-club')) {
  customElements.define(
    'lph-cart-club',
    class extends HTMLElement {
      connectedCallback() {
        this.checkbox = this.querySelector('[data-join]');
        this.checkbox.addEventListener('change', () => this.save());
        const info = this.querySelector('[data-club-info]');
        info?.addEventListener('click', () => {
          const panel = this.querySelector('[data-club-more]');
          panel.hidden = !panel.hidden;
          info.setAttribute('aria-expanded', String(!panel.hidden));
        });
        if (this.dataset.signedIn === 'true') this.loadMember();
        else {
          if (window.lphKnownCard) this.showKnown(window.lphKnownCard);
          this.onKnown = (e) => this.showKnown(e.detail);
          window.addEventListener('lph:known', this.onKnown);
        }
      }

      disconnectedCallback() {
        if (this.onKnown) window.removeEventListener('lph:known', this.onKnown);
      }

      // Signed out but remembered on this device: greet them and show their next stamp.
      showKnown(card) {
        if (!card?.member || this.dataset.earns !== 'true') return;
        const next = Math.min(card.stamps + 1, card.stamps_per_reward);
        const hi = card.first_name ? `Hi ${card.first_name}! ` : '';
        const progress = this.querySelector('[data-progress]');
        progress.textContent =
          next === card.stamps_per_reward
            ? `✨ ${hi}This order completes your Little Paper Club card, so your next card is free! `
            : `✨ ${hi}This order earns stamp ${next} of ${card.stamps_per_reward} on your Little Paper Club card. `;
        progress.querySelector('.lph-cart-club__not-you')?.remove();
        const notYou = document.createElement('button');
        notYou.type = 'button';
        notYou.className = 'lph-cart-club__not-you';
        notYou.textContent = card.first_name ? `Not ${card.first_name}?` : 'Not you?';
        notYou.addEventListener('click', () => window.lphForget?.());
        progress.append(notYou);
        progress.hidden = false;
        this.querySelector('[data-optin]').hidden = true;
      }

      async save() {
        this.checkbox.disabled = true;
        try {
          await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ attributes: { _loyalty_join: this.checkbox.checked ? 'yes' : '' } }),
          });
        } finally {
          this.checkbox.disabled = false;
        }
      }

      async loadMember() {
        try {
          const res = await fetch('/apps/reminders/loyalty', { headers: { Accept: 'application/json' } });
          if (!res.ok) return;
          const card = await res.json();
          if (!card.member || this.dataset.earns !== 'true') return;
          const next = Math.min(card.stamps + 1, card.stamps_per_reward);
          const progress = this.querySelector('[data-progress]');
          progress.textContent =
            next === card.stamps_per_reward
              ? `✨ This order completes your Little Paper Club card, so your next card is free!`
              : `✨ This order earns stamp ${next} of ${card.stamps_per_reward} on your Little Paper Club card.`;
          progress.hidden = false;
          this.querySelector('[data-optin]').hidden = true;
        } catch {
          // Keep the opt-in visible if we can't tell.
        }
      }
    }
  );
}

if (!customElements.get('lph-cart-post')) {
  customElements.define(
    'lph-cart-post',
    class extends HTMLElement {
      connectedCallback() {
        // The drawer can detach and re-attach this element: wire it up once.
        if (this.wired) return;
        this.wired = true;
        const form = this.querySelector('[data-form]');
        // Enter would submit the basket form (= Checkout) with the old address: save instead.
        // (Not in the address finder, where Enter picks a suggestion.)
        form.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter' || event.target.matches('[data-finder]')) return;
          event.preventDefault();
          this.querySelector('[data-save]').click();
        });
        const address = this.querySelector('lph-address');
        this.querySelector('[data-edit]').addEventListener('click', () => {
          form.hidden = false;
          address.activate();
          address.nameInput.focus();
        });
        this.querySelector('[data-cancel]').addEventListener('click', () => (form.hidden = true));
        // "Post to me instead": no address, and the card's plain message version (posted versions carry
        // their postage: scripts/setup_posting_option.py).
        this.querySelector('[data-remove]').addEventListener('click', () =>
          this.save(null, 'Add a message (+£0.80)', { 'Arrive by': null, _post_by: null }),
        );
        this.querySelector('[data-postage]')?.addEventListener('click', (e) => this.switchPostage(e.currentTarget.dataset.postage));
        this.setupDate();
        this.querySelector('[data-save]').addEventListener('click', () => {
          const to = address.value();
          // "Save this address for next time" unticked: the address book skips it.
          if (to) this.save(to, undefined, { _save_address: address.saveAddress() ? null : 'no' });
        });
      }

      // ---- Arriving by a date (assets/lph-post-dates.js) ------------------------------------------

      /** The card's arrive-by day (YYYY-MM-DD) from its "Arrive by" text ("Thu 24 Dec 2026"). */
      arriveBy() {
        const text = this.querySelector('.lph-cart-post__when')?.textContent ?? '';
        const m = text.match(/(\d{1,2}) (Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w* (\d{4})/);
        if (!m) return null;
        const month = 'JanFebMarAprMayJunJulAugSepOctNovDec'.indexOf(m[2]) / 3 + 1;
        return `${m[3]}-${String(month).padStart(2, '0')}-${m[1].padStart(2, '0')}`;
      }

      setupDate() {
        const dates = window.LphPostDates;
        const form = this.querySelector('[data-date-form]');
        if (!dates || !form) return;
        const kind = dates.postage(this.dataset.posting);
        const input = form.querySelector('[data-arrive-by]');
        const hint = form.querySelector('[data-date-hint]');
        // iPhones' date picker ignores min/max: a bad date is explained, and cleared on leaving the box.
        const show = (clearBad = false) => {
          const said = dates.explain(input.value, kind);
          if (said.bad && !said.keep && clearBad) {
            input.value = '';
          }
          hint.textContent = said.text;
          hint.classList.toggle('lph-cart-post__error', said.bad);
          return said.bad;
        };
        const open = () => {
          form.hidden = false;
          input.min = dates.earliest(kind);
          input.max = dates.latest();
          show();
          input.focus();
        };
        this.querySelector('[data-edit-date]').addEventListener('click', open);
        input.addEventListener('change', () => show());
        input.addEventListener('blur', () => show(true));
        input.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          this.querySelector('[data-save-date]').click();
        });
        this.querySelector('[data-cancel-date]').addEventListener('click', () => (form.hidden = true));
        this.querySelector('[data-save-date]').addEventListener('click', () => {
          if (!input.value || show()) {
            if (!input.value) hint.textContent = 'Please choose a date.';
            hint.classList.add('lph-cart-post__error');
            return input.focus();
          }
          this.save(undefined, undefined, { 'Arrive by': dates.format(input.value), _post_by: dates.postBy(input.value, kind) });
        });
        this.querySelector('[data-soon]')?.addEventListener('click', () => this.save(undefined, undefined, { 'Arrive by': null, _post_by: null }));

        // Left in the basket too long: the date can't be met any more.
        const postBy = this.dataset.postBy;
        if (postBy && postBy < dates.soonestPost()) {
          const stale = this.querySelector('[data-date-stale]');
          stale.textContent = "It's now too late to get this card there by that date. Please choose a new date, or send it as soon as possible.";
          stale.hidden = false;
          open();
        }
      }

      /** Other postage: same card, and a date it has is worked out again (or refused if too soon). */
      switchPostage(posting) {
        const dates = window.LphPostDates;
        const arriveBy = this.arriveBy();
        if (!dates || !arriveBy) return this.save(undefined, posting);
        const kind = dates.postage(posting);
        const problem = dates.check(arriveBy, kind);
        if (problem) {
          const box = this.querySelector('[data-date-stale]');
          box.textContent = `${kind === 'first' ? '1st' : '2nd'} Class can't get it there by ${dates.format(arriveBy, { year: false })}. ${problem} Change the date first.`;
          box.hidden = false;
          return;
        }
        this.save(undefined, posting, { _post_by: dates.postBy(arriveBy, kind) });
      }

      error(message, input) {
        // Shown in the address form when it's open, otherwise under the card's details.
        const box = this.querySelector(this.querySelector('[data-form]').hidden ? '[data-date-stale]' : '[data-error]');
        box.textContent = message;
        box.hidden = !message;
        input?.focus();
      }

      /**
       * Set (or with null, remove) the card's "Posting to", and with `posting` switch its "Message
       * inside" option to another value (the message, or posted 2nd / 1st Class); then show the
       * fresh basket. to === undefined keeps the address as it is. `changes`: other properties to set
       * (null removes one).
       */
      async save(to, posting, changes = {}) {
        this.querySelectorAll('button').forEach((b) => (b.disabled = true));
        try {
          // The card's other answers, raw from the basket (Liquid's copies are HTML-escaped, and
          // saving those back would turn "Jo & Sam" into "Jo &amp; Sam").
          const root = window.Shopify?.routes?.root ?? '/';
          const post = (url, body) =>
            fetch(`${root}${url}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify(body),
            }).then((r) => {
              if (!r.ok) throw new Error(String(r.status));
              return r.json();
            });
          const cart = await (await fetch(`${root}cart.js`)).json();
          const line = cart.items.find((i) => i.key === this.dataset.key);
          if (!line) throw new Error('line not found');
          const properties = { ...line.properties };
          if (to) properties['Posting to'] = to;
          else if (to === null) delete properties['Posting to'];
          for (const [key, value] of Object.entries(changes)) {
            if (value == null) delete properties[key];
            else properties[key] = value;
          }
          const current = line.options_with_values?.find((o) => o.name === 'Message inside')?.value;
          if (posting && current && posting !== current) {
            // Same card, other version: add it with the same answers, then remove this line.
            const product = await (await fetch(`${root}products/${line.handle}.js`)).json();
            const wanted = (line.options_with_values ?? []).map((o) => (o.name === 'Message inside' ? posting : o.value));
            const variant = product.variants.find((v) => v.options.every((value, i) => value === wanted[i]));
            if (!variant) throw new Error('no such version');
            await post('cart/add.js', { items: [{ id: variant.id, quantity: line.quantity, properties }] });
            await post('cart/change.js', { id: this.dataset.key, quantity: 0 });
          } else {
            await post('cart/change.js', { id: this.dataset.key, quantity: Number(this.dataset.quantity), properties });
          }
          location.reload();
        } catch {
          this.error("Sorry, that didn't save. Please try again.");
          this.querySelectorAll('button').forEach((b) => (b.disabled = false));
        }
      }
    }
  );
}
